/**
 * @fileoverview The Active Clients grid.
 * @description The header (Tenant ID, Client name, Branches and Client since sort through the
 * backend; the others are plain labels) and one ClientRow per client. The empty states tell "no
 * client matches these filters" from "no client yet".
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import ClientRow, { CLIENT_GRID_COLUMNS } from './ClientRow'
import TableEmptyState from './TableEmptyState'

import type { ClientSort } from '@/features/sales/lib/sales-clients.utils'
import type {
  ClientListItem,
  SalesClientSortField,
} from '@/features/sales/types/sales-client.types'

interface ClientsTableProps {
  clients: ClientListItem[]
  sort: ClientSort
  anyFilter: boolean
  onSort: (field: SalesClientSortField) => void
  onView: (client: ClientListItem) => void
  onClear: () => void
}

/** The columns, in order; `field` is set on the ones the backend can sort by. */
const COLUMNS: { key: string; field?: SalesClientSortField }[] = [
  { key: 'tenantId', field: 'tenantCode' },
  { key: 'clientName', field: 'bankName' },
  { key: 'location' },
  { key: 'tier' },
  { key: 'branches', field: 'branchCount' },
  { key: 'owner' },
  { key: 'clientSince', field: 'onboardedAt' },
  { key: 'hrAdmin' },
  { key: 'status' },
  { key: 'actions' },
]

/**
 * @description The `aria-sort` of a sortable column.
 * @param {ClientSort} sort - The sort now.
 * @param {SalesClientSortField} field - The column.
 * @returns {'ascending' | 'descending' | 'none'} How the column is sorted.
 */
function ariaSortOf(sort: ClientSort, field: SalesClientSortField) {
  if (sort.field !== field) return 'none'
  return sort.direction === 'asc' ? 'ascending' : 'descending'
}

/**
 * @component
 * @param {ClientsTableProps} props - The rows, the sort and the handlers.
 * @param {ClientListItem[]} props.clients - The page's clients.
 * @param {ClientSort} props.sort - The sort now.
 * @param {boolean} props.anyFilter - Whether any filter is set (picks the empty state).
 * @param {(field: SalesClientSortField) => void} props.onSort - Sorts by a column.
 * @param {(client: ClientListItem) => void} props.onView - Opens a client's details.
 * @param {() => void} props.onClear - Clears every filter.
 */
function ClientsTable({ clients, sort, anyFilter, onSort, onView, onClear }: ClientsTableProps) {
  const { t } = useTranslation()

  return (
    <div className="overflow-x-auto border-t border-divider">
      <div className="min-w-[1280px]">
        <div
          className={`grid ${CLIENT_GRID_COLUMNS} items-center gap-3 bg-input-bg px-4 py-2.5 sm:px-5`}
        >
          {COLUMNS.map(({ key, field }) => {
            const label = t(`sales.activeClients.columns.${key}`)
            const className = `text-caption font-semibold tracking-caption text-table-header ${
              key === 'actions' ? 'text-right' : ''
            }`
            if (!field) {
              return (
                <span key={key} className={className}>
                  {label}
                </span>
              )
            }
            const active = sort.field === field
            return (
              <span key={key} aria-sort={ariaSortOf(sort, field)}>
                <button
                  type="button"
                  onClick={() => onSort(field)}
                  data-testid={`client-sort-${field}`}
                  className={`${className} cursor-pointer hover:text-ink`}
                >
                  {label}
                  {active && (sort.direction === 'asc' ? ' ▲' : ' ▼')}
                </button>
              </span>
            )
          })}
        </div>

        {clients.map((client) => (
          <ClientRow key={client.tenantId} client={client} onView={onView} />
        ))}

        {clients.length === 0 &&
          (anyFilter ? (
            <TableEmptyState
              message={t('sales.activeClients.noResults')}
              clearLabel={t('sales.activeClients.noResultsClear')}
              onClear={onClear}
            />
          ) : (
            <TableEmptyState
              message={t('sales.activeClients.noClients')}
              description={t('sales.activeClients.noClientsDescription')}
              icon="briefcase"
            />
          ))}
      </div>
    </div>
  )
}

export default ClientsTable
